import React, { useState, useEffect, useMemo } from 'react';
import { 
  BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer 
} from 'recharts';
import { 
  Grid, Card, Button, Typography, Box, CircularProgress, useTheme, 
  MenuItem, Select, FormControl, InputLabel, IconButton, Dialog,
  DialogTitle, DialogContent, DialogActions, TextField, useMediaQuery,
  Stack, Paper, Chip, Container, Divider, SwipeableDrawer, List, ListItem, ListItemText,
  Fab, Zoom, Fade, Grow
} from '@mui/material';
import {
  AttachMoney, Receipt, AccountBalance, ShowChart,
  RestaurantMenu, BusinessCenter, People, AccountBalanceWallet,
  ShoppingCart, Warning, LibraryBooks, MoneyOff, TrendingDown, TrendingUp,
  Edit, Delete, Menu as MenuIcon, Close, Refresh, FilterList
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { getFirestore, collection, query, where, getDocs, doc, updateDoc, deleteDoc } from 'firebase/firestore';
import { signOut } from 'firebase/auth';
import { onAuthStateChanged, getAuth } from "firebase/auth";
import { auth } from "../Firebase/Config";
import AddRestaurantFormPopup from '../Components/PopUpAddResto';
import { styled } from '@mui/system';
import RapportX from '../Services/RapportX';
import RH from '../Services/RH';
import Facture from '../Services/FactureFournisseur';
import EncaissementService from "../Services/Encaissement";
import DecaissementService from "../Services/Decaissement";

// Custom styled components with responsive adjustments
const GradientCard = styled(Card)(({ theme }) => ({
  padding: theme.spacing(2),
  [theme.breakpoints.up('sm')]: {
    padding: theme.spacing(3),
  },
  borderRadius: '16px',
  background: `linear-gradient(145deg, ${theme.palette.background.paper}, #f8f9ff)`,
  boxShadow: '0 8px 32px rgba(0,0,0,0.05)',
  transition: 'transform 0.3s ease, box-shadow 0.3s ease',
  '&:hover': {
    transform: 'translateY(-5px)',
    boxShadow: '0 12px 48px rgba(0,0,0,0.1)'
  }
}));

const StatIconWrapper = styled('div')(({ theme, color }) => ({
  width: 40,
  [theme.breakpoints.up('sm')]: {
    width: 48,
  },
  height: 40,
  [theme.breakpoints.up('sm')]: {
    height: 48,
  },
  borderRadius: '12px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  background: `linear-gradient(135deg, ${color}30, ${color}10)`,
  color: color,
  marginRight: theme.spacing(1.5),
  [theme.breakpoints.up('sm')]: {
    marginRight: theme.spacing(2),
  }
}));

const StyledCard = styled(Card)(({ theme }) => ({
  padding: theme.spacing(2),
  [theme.breakpoints.up('sm')]: {
    padding: theme.spacing(3),
  },
  borderRadius: 20,
  boxShadow: '0 8px 30px rgba(0,0,0,0.05)',
  transition: '0.25s',
  '&:hover': {
    transform: 'translateY(-5px)',
    boxShadow: '0 12px 40px rgba(0,0,0,0.1)'
  }
}));

// Chart color scheme
const chartColors = {
  primary: '#6366f1',
  secondary: '#10b981'
};

// Icon colors
const iconColors = {
  primary: '#6366f1',
  secondary: '#10b981',
  red: '#ef4444',
  amber: '#f59e0b',
  purple: '#8b5cf6',
  blue: '#3b82f6',
  pink: '#ec4899',
  teal: '#14b8a6',
  orange: '#f97316',
  slate: '#64748b',
  lime: '#84cc16',
  rose: '#f43f5e'
};

// Sample data for charts
const data = [
  { name: 'Jan', sales: 4000, profit: 2400 },
  { name: 'Feb', sales: 3000, profit: 1398 },
  { name: 'Mar', sales: 2000, profit: 9800 },
  { name: 'Apr', sales: 2780, profit: 3908 },
  { name: 'May', sales: 1890, profit: 4800 },
  { name: 'Jun', sales: 2390, profit: 3800 },
  { name: 'Jul', sales: 3490, profit: 4300 },
];

function Dashboard() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.between('sm', 'md'));
  
  const [encaissements, setEncaissements] = useState([]);
  const [decaissements, setDecaissements] = useState([]); 
  const [totalDesimpyé, setTotalDesimpyé] = useState(0);
  const [rapportXValues, setRapportXValues] = useState({});
  const [totalRapportX, setTotalRapportX] = useState(0);
  const [restaurants, setRestaurants] = useState([]);
  const [currentUser, setCurrentUser] = useState(null);
  const [totalHTVA, setTotalHTVA] = useState(0);
  const [totalTTC, setTotalTTC] = useState(0);
  const [TotalTVADeductible, setTotalTVADeductible] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [totalAccompte, setTotalAccompte] = useState(null);
  const user = JSON.parse(localStorage.getItem("user"));
  const navigate = useNavigate();
  const restaurantId = localStorage.getItem("restaurantId");

  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth());
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [totalSalary, setTotalSalary] = useState(0);
  const [editingRestaurant, setEditingRestaurant] = useState(null);
  const [editFormData, setEditFormData] = useState({
    name: '',
    address: '',
    phone: '',
    imageUrl: ''
  });
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [mobileFilterOpen, setMobileFilterOpen] = useState(false);
  const [activeTab, setActiveTab] = useState(0);

  // Mobile filter drawer toggle
  const toggleMobileFilter = () => {
    setMobileFilterOpen(!mobileFilterOpen);
  };

  // Open edit dialog and initialize form data
  const handleEditClick = (restaurant) => {
    setEditingRestaurant(restaurant);
    setEditFormData({
      name: restaurant.name,
      address: restaurant.address || '',
      phone: restaurant.phone || '',
      imageUrl: restaurant.imageUrl || ''
    });
    setIsEditDialogOpen(true);
  };

  // Close edit dialog
  const handleEditClose = () => {
    setIsEditDialogOpen(false);
    setEditingRestaurant(null);
  };

  // Handle form input changes
  const handleEditFormChange = (e) => {
    const { name, value } = e.target;
    setEditFormData(prev => ({ ...prev, [name]: value }));
  };

  // Submit restaurant edit
  const handleEditSubmit = async () => {
    if (!editingRestaurant) return;
    
    try {
      const db = getFirestore();
      const restaurantRef = doc(db, "restaurants", editingRestaurant.id);
      await updateDoc(restaurantRef, editFormData);
      
      setRestaurants(restaurants.map(r => 
        r.id === editingRestaurant.id ? { ...r, ...editFormData } : r
      ));
      
      setIsEditDialogOpen(false);
    } catch (error) {
      console.error("Error updating restaurant:", error);
      alert("Failed to update restaurant");
    }
  };

  // Delete restaurant
  const handleDeleteRestaurant = async (restaurantId) => {
    if (window.confirm("Êtes-vous sûr de vouloir supprimer ce restaurant?")) {
      try {
        const db = getFirestore();
        await deleteDoc(doc(db, "restaurants", restaurantId));
        
        setRestaurants(restaurants.filter(r => r.id !== restaurantId));
        
        if (localStorage.getItem('restaurantId') === restaurantId) {
          localStorage.removeItem('restaurantId');
          if (restaurants.length > 1) {
            localStorage.setItem('restaurantId', restaurants[0].id);
          }
        }
      } catch (error) {
        console.error("Error deleting restaurant:", error);
        alert("Échec de la suppression du restaurant");
      }
    }
  };

  // Process financial data for charts
  const processFinancialData = useMemo(() => {
    if (!encaissements.length || !decaissements.length) return [];
    
    const daysInMonth = new Date(selectedYear, selectedMonth + 1, 0).getDate();
    // For mobile, show fewer data points or aggregate by week
    const step = isMobile ? Math.ceil(daysInMonth / 7) : 1;
    
    return Array.from({ length: daysInMonth }, (_, i) => {
      const day = i + 1;
      const dateKey = `${selectedYear}-${String(selectedMonth + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
      
      const revenue = encaissements
        .filter(e => e.restaurantId === restaurantId && 
          new Date(e.date).toISOString().split('T')[0] === dateKey)
        .reduce((sum, e) => sum + (Number(e.value) || 0), 0);

      const expenses = decaissements
        .filter(d => d.restaurantId === restaurantId && 
          new Date(d.date).toISOString().split('T')[0] === dateKey)
        .reduce((sum, d) => sum + (Number(d.value) || 0), 0);

      return {
        name: isMobile ? `J${day}` : `J${day}`,
        date: dateKey,
        revenue: revenue,
        expenses: Math.abs(expenses),
        balance: revenue - expenses
      };
    });
  }, [encaissements, decaissements, restaurantId, selectedMonth, selectedYear, isMobile]);

  // Calculate totals
  const { totalRevenue, totalExpenses, netProfit } = useMemo(() => ({
    totalRevenue: processFinancialData.reduce((sum, d) => sum + d.revenue, 0),
    totalExpenses: processFinancialData.reduce((sum, d) => sum + d.expenses, 0),
    netProfit: processFinancialData.reduce((sum, d) => sum + d.balance, 0)
  }), [processFinancialData]);

  // Custom tooltip component
  const CustomTooltip = ({ active, payload, label }) => {
    if (!active || !payload) return null;

    return (
      <div style={{
        background: theme.palette.background.paper,
        padding: theme.spacing(1.5),
        [theme.breakpoints.up('sm')]: {
          padding: theme.spacing(2),
        },
        borderRadius: '8px',
        boxShadow: theme.shadows[3],
        border: `1px solid ${theme.palette.divider}`
      }}>
        <Typography variant="subtitle2" fontWeight={600} fontSize={isMobile ? 12 : 14}>{label}</Typography>
        {payload.map((entry, index) => (
          <div key={index} style={{ 
            display: 'flex', 
            alignItems: 'center',
            gap: theme.spacing(1),
            marginTop: theme.spacing(0.5)
          }}>
            <div style={{
              width: 10,
              height: 10,
              backgroundColor: entry.color,
              borderRadius: 3
            }}/>
            <Typography variant="body2" fontSize={isMobile ? 11 : 13}>
              {entry.name}: {entry.value.toLocaleString('fr-FR', { minimumFractionDigits: 2 })} TND
            </Typography>
          </div>
        ))}
      </div>
    );
  };

  useEffect(() => {
    const fetchTotalHTVA = async () => {
      try {
        const result = await Facture.getTotalAchatHTVAByRestaurantId(
          restaurantId,
          selectedMonth,
          selectedYear
        );
        setTotalHTVA(result);
      } catch (error) {
        console.error("Error fetching total Achat HTVA:", error);
      }
    };
  
    fetchTotalHTVA();
  }, [restaurantId, selectedMonth, selectedYear]);

  useEffect(() => {
    const fetchTotalTVADeductible = async () => {
      try {
        const result = await Facture.getTotalTVADeductibleByRestaurantId(
          restaurantId,
          selectedMonth,
          selectedYear
        );
        setTotalTVADeductible(result);
      } catch (error) {
        console.error("Error fetching total Achat HTVA:", error);
      }
    };
  
    fetchTotalTVADeductible();
  }, [restaurantId, selectedMonth, selectedYear]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [encaissementsData, decaissementsData] = await Promise.all([
          EncaissementService.getAllEncaissements(),
          DecaissementService.getAllDecaissements()
        ]);
        
        setEncaissements(encaissementsData);
        setDecaissements(decaissementsData);
      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);
  
  const combinedData = useMemo(() => [
    ...encaissements
      .filter(e => e.restaurantId === restaurantId)
      .map(e => ({ ...e, category: "ENCAISSEMENT" })),
    ...decaissements
      .filter(d => d.restaurantId === restaurantId)
      .map(d => ({ ...d, category: "DECAISSEMENT" }))
  ], [encaissements, decaissements, restaurantId]);

  const now = new Date();
  const firstDayOfMonth = new Date(Date.UTC(selectedYear, selectedMonth, 1));
  const lastDayOfMonth = new Date(Date.UTC(selectedYear, selectedMonth + 1, 0));
  lastDayOfMonth.setUTCHours(23, 59, 59, 999);
  
  const totalEncaissements = (Array.isArray(encaissements) ? encaissements : [])
    .filter(e =>
      e.restaurantId === restaurantId &&
      e.date && new Date(e.date) >= firstDayOfMonth && new Date(e.date) <= lastDayOfMonth
    )
    .reduce((sum, e) => sum + (e.value || 0), 0);
  
  const totalDecaissements = (Array.isArray(decaissements) ? decaissements : [])
    .filter(d =>
      d.restaurantId === restaurantId &&
      d.date && new Date(d.date) >= firstDayOfMonth && new Date(d.date) <= lastDayOfMonth
    )
    .reduce((sum, d) => sum + (d.value || 0), 0);
  
  const solde = totalEncaissements + totalDecaissements;

  useEffect(() => {
    const fetchTotalDesimpyé = async () => {
      try {
        setLoading(true);
        const total = await Facture.getTotalDesimpyéByRestaurantId(restaurantId, selectedMonth, selectedYear);
        setTotalDesimpyé(total);
      } catch (err) {
        setError("Failed to fetch total reste à payer.");
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    fetchTotalDesimpyé();
  }, [restaurantId, selectedMonth, selectedYear]);
  
  useEffect(() => {
    const fetchTotalDesTTC = async () => {
      try {
        setLoading(true);
        const total = await Facture.getTotalTTCByRestaurantId(restaurantId, selectedMonth, selectedYear);
        setTotalTTC(total);
      } catch (err) {
        setError("Failed to fetch total reste à payer.");
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    fetchTotalDesTTC();
  }, [restaurantId, selectedMonth, selectedYear]);
  
  useEffect(() => {
    const fetchTotalSalary = async () => {
      try {
        if (restaurantId) {
          const salary = await RH.getTotalSalaryByRestaurantId(restaurantId);
          setTotalSalary(salary);
        }
      } catch (error) {
        console.error("Failed to fetch total salary:", error.message);
      } finally {
        setLoading(false);
      }
    };

    fetchTotalSalary();
  }, [restaurantId]);

  const fetchRapportX = async () => {
    try {
      const allRapportX = await RapportX.getAllRapportX(restaurantId);
      const now = new Date();
      const currentMonth = now.getMonth();
      const currentYear = now.getFullYear();

      const filteredRapportX = allRapportX.filter(item => {
        const itemDate = new Date(item.date);
        return (
          itemDate.getMonth() === currentMonth &&
          itemDate.getFullYear() === currentYear
        );
      });

      const mappedValues = filteredRapportX.reduce((acc, item) => {
        acc[item.date] = item.value;
        return acc;
      }, {});

      setRapportXValues(mappedValues);
    } catch (error) {
      console.error("Error fetching RapportX:", error.message);
    }
  };

  const fetchTotalRapportX = async () => {
    try {
      const allRapportX = await RapportX.getAllRapportX(restaurantId);

      const filteredRapportX = allRapportX.filter(item => {
        const itemDate = new Date(item.date);
        return (
          itemDate.getMonth() === selectedMonth &&
          itemDate.getFullYear() === selectedYear
        );
      });

      const total = filteredRapportX.reduce((sum, item) => sum + Number(item.value), 0);
      setTotalRapportX(total);
    } catch (error) {
      console.error("Error fetching total RapportX:", error.message);
    }
  };

  useEffect(() => {
    fetchRapportX();
    fetchTotalRapportX();

    const interval = setInterval(() => {
      fetchRapportX();
      fetchTotalRapportX();
    }, 30000);

    return () => clearInterval(interval);
  }, [restaurantId, selectedMonth, selectedYear]);

  const fetchRestaurants = async () => {
    try {
      setLoading(true);
      const db = getFirestore();
      const auth = getAuth();
      const user = auth.currentUser;
      if (!user) throw new Error('User not logged in');
  
      const restaurantsRef = collection(db, 'restaurants');
      const q = query(restaurantsRef, where('ownerId', '==', user.uid));
      const querySnapshot = await getDocs(q);
      const restaurantData = querySnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
  
      setRestaurants(restaurantData);
  
      if (restaurantData.length > 0 && !localStorage.getItem('restaurantId')) {
        localStorage.setItem('restaurantId', restaurantData[0].id);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRestaurants();
    const interval = setInterval(() => {
      fetchRestaurants();
    }, 30000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (user && user.isVerifed === false) {
        navigate('/addResto');
      }
    });
    return unsubscribe;
  }, []);

  const handleLogout = async () => {
    try {
      await signOut(auth);
      localStorage.removeItem('user');
      localStorage.removeItem('userToken');
      sessionStorage.removeItem('user');
      sessionStorage.removeItem('userToken');
      navigate('/');  
    } catch (error) {
      console.error("Logout failed: ", error.message);
    }
  };

  const totalHT = (totalRapportX * 0.93).toFixed(3);
  
  useEffect(() => {
    const fetchTotalAccompte = async () => {
      setLoading(true);
      try {
        const total = await RH.getTotalAccompteByRestaurantId(
          restaurantId,
          selectedMonth,
          selectedYear
        );
        setTotalAccompte(total);
      } catch (error) {
        console.error("Failed to fetch total accompte:", error);
      } finally {
        setLoading(false);
      }
    };
  
    if (restaurantId !== null) {
      fetchTotalAccompte();
    }
  }, [restaurantId, selectedMonth, selectedYear]);
  
  const margeBrut = useMemo(() => {
    const ht = parseFloat(totalHT);
    const tva = parseFloat(totalHTVA);
    const tsalary = parseFloat(totalSalary);
    if (!ht || !tsalary || !tva || ht === 0 || isNaN(ht) || isNaN(tsalary) || isNaN(tva)) return 0;
  
    if (selectedMonth && selectedYear) {
      return ((ht - ((parseFloat(totalHTVA) || 0) + (parseFloat(totalSalary) || 0)).toFixed(3)) / ht) * 100;
    }
    return 0;
  }, [totalHT, totalHTVA, totalSalary, selectedMonth, selectedYear]);

  const CoutTotalDexploitation = useMemo(() => {
    const ttc = parseFloat(totalTTC);
    const salaireNet = parseFloat(totalSalary);
  
    if (
      isNaN(ttc) || isNaN(salaireNet) ||
      ttc === 0 || salaireNet === 0 ||
      !selectedMonth || !selectedYear
    ) {
      return { value: 0, message: "" };
    }
  
    const result = ttc + salaireNet;
    const message =
      result >= 75
        ? "⚠️ Il faut optimiser les coûts."
        : "✅ Le restaurant est bien géré.";
  
    return { value: result, message };
  }, [totalTTC, totalSalary, selectedMonth, selectedYear]);
  
  const FoodCostGlobal = useMemo(() => {
    const ht = parseFloat(totalHT);
    const tva = parseFloat(totalHTVA);
  
    if (!ht || !tva || ht === 0 || isNaN(ht) || isNaN(tva)) return 0;
  
    if (selectedMonth && selectedYear) {
      return parseFloat(((tva / ht) * 100).toFixed(3));
    }
    return 0;
  }, [totalHT, totalHTVA, selectedMonth, selectedYear]);

  const totalTTCValue = parseEuropeanNumber(totalTTC);
  
  function parseEuropeanNumber(str) {
    if (typeof str !== 'string') return 0;
    str = str.replace(/\s/g, '').replace(/\u202F/g, '');
    str = str.replace(',', '.');
    const num = parseFloat(str);
    return isNaN(num) ? 0 : num;
  }

  // KPI Data for mapping
  const kpiData = [
    { title: "Chiffre D'affaire TTC", value: `${totalRapportX.toFixed(3)} TND`, icon: <AttachMoney />, color: "#8b5cf6" },
    { title: "Chiffre D'affaire HTVA", value: `${totalHT} TND`, icon: <Receipt />, color: "#22c55e" },
    { title: "Chiffre D'affaire Réel TTC", value: `${solde.toFixed(3)} TND`, icon: <AccountBalance />, color: "#ef4444" },
    { title: "Taux de Marge Brut", value: `${margeBrut.toFixed(2)}%`, icon: <ShowChart />, color: "#f59e0b" },
    { title: "Food Cost Global", value: `${Math.round(FoodCostGlobal)}%`, icon: <RestaurantMenu />, color: "#3b82f6" },
    { title: "Coût Total d'exploitation", value: `${((parseFloat(totalHTVA) || 0) + (parseFloat(totalSalary) || 0)).toFixed(3)} TND`, icon: <BusinessCenter />, color: "#6366f1" },
    { title: "Charge Salariale", value: `${totalSalary.toFixed(2)} TND`, icon: <People />, color: "#ec4899" },
    { title: "Total Accompte", value: `${totalAccompte || 0} TND`, icon: <AccountBalanceWallet />, color: "#14b8a6" },
    { title: "Total Des Achat", value: `${totalTTC} TND`, icon: <ShoppingCart />, color: "#f97316" },
    { title: "Total Des Impayés", value: `${totalDesimpyé} TND`, icon: <Warning />, color: "#64748b" },
    { title: "TVA Collecté", value: `${(totalRapportX * 0.07).toFixed(3)} DT`, icon: <LibraryBooks />, color: "#84cc16" },
    { title: "TVA Déductible", value: `${TotalTVADeductible}`, icon: <MoneyOff />, color: "#f43f5e" }
  ];

  // Split KPI into two rows for mobile
  const topKpis = kpiData.slice(0, 6);
  const bottomKpis = kpiData.slice(6, 12);

  // Loading State
  if (loading) {
    return (
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        minHeight: '100vh',
        flexDirection: 'column',
        gap: 2
      }}>
        <CircularProgress sx={{ color: '#6366f1' }} />
        <Typography variant="body2" color="text.secondary">Chargement de votre tableau de bord...</Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        p: { xs: 1.5, sm: 2, md: 4 },
        background: "#f5f7fb",
        minHeight: "100vh"
      }}
    >
      {/* HEADER */}
      <Box
        sx={{
          mb: { xs: 3, sm: 4, md: 5 },
          display: "flex",
          flexDirection: { xs: "column", md: "row" },
          justifyContent: "space-between",
          alignItems: { xs: "flex-start", md: "center" },
          gap: 2
        }}
      >
        <Box>
          <Typography 
            variant={isMobile ? "h5" : "h4"} 
            fontWeight={700}
            sx={{ fontSize: { xs: '1.5rem', sm: '2rem' } }}
          >
            Heureux de vous revoir, {user?.prenom} {user?.nom}
          </Typography>
          <Typography 
            color="text.secondary"
            sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}
          >
            Suivez en un coup d'œil les performances de votre restaurant.
          </Typography>
        </Box>

        <AddRestaurantFormPopup />
      </Box>

      {/* MONTH / YEAR FILTER - Mobile Friendly */}
      <Box sx={{ 
        display: "flex", 
        gap: 2, 
        mb: { xs: 3, sm: 4 },
        flexDirection: { xs: "column", sm: "row" },
        alignItems: { xs: "stretch", sm: "center" }
      }}>
        <Box sx={{ display: "flex", gap: 2, flex: 1 }}>
          <FormControl size="small" fullWidth={isMobile}>
            <InputLabel>Mois</InputLabel>
            <Select
              value={selectedMonth}
              label="Mois"
              onChange={(e) => setSelectedMonth(Number(e.target.value))}
            >
              {Array.from({ length: 12 }, (_, i) => (
                <MenuItem key={i} value={i}>
                  {new Date(0, i).toLocaleString("fr-FR", { month: "long" })}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl size="small" fullWidth={isMobile}>
            <InputLabel>Année</InputLabel>
            <Select
              value={selectedYear}
              label="Année"
              onChange={(e) => setSelectedYear(Number(e.target.value))}
            >
              {Array.from({ length: 5 }, (_, i) => {
                const year = new Date().getFullYear() - i;
                return <MenuItem key={year} value={year}>{year}</MenuItem>;
              })}
            </Select>
          </FormControl>
        </Box>
        
        {isMobile && (
          <Fab 
            size="small" 
            color="primary" 
            sx={{ position: 'fixed', bottom: 16, right: 16, zIndex: 1000 }}
            onClick={toggleMobileFilter}
          >
            <FilterList />
          </Fab>
        )}
      </Box>

      {/* KPI CARDS - Responsive Grid */}
      <Box sx={{ mb: { xs: 4, sm: 6 } }}>
        {/* For desktop: show all cards in grid */}
        {!isMobile && (
          <Grid container spacing={3}>
            {kpiData.map((stat, i) => (
              <Grid key={i} item xs={12} sm={6} md={4} lg={3}>
                <StyledCard>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                    <Box
                      sx={{
                        width: 48,
                        height: 48,
                        borderRadius: 3,
                        background: `${stat.color}20`,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        color: stat.color
                      }}
                    >
                      {stat.icon}
                    </Box>
                    <Box>
                      <Typography fontSize={13} color="text.secondary">
                        {stat.title}
                      </Typography>
                      <Typography fontWeight={700}>
                        {stat.value}
                      </Typography>
                    </Box>
                  </Box>
                </StyledCard>
              </Grid>
            ))}
          </Grid>
        )}

        {/* For mobile: show scrollable horizontal cards */}
        {isMobile && (
          <>
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                Indicateurs clés
              </Typography>
              <Box sx={{ overflowX: 'auto', display: 'flex', gap: 2, pb: 1 }}>
                {kpiData.map((stat, i) => (
                  <Paper
                    key={i}
                    elevation={0}
                    sx={{
                      minWidth: 180,
                      p: 2,
                      borderRadius: 3,
                      background: `linear-gradient(135deg, ${stat.color}08, ${stat.color}02)`,
                      border: `1px solid ${stat.color}20`,
                      flexShrink: 0
                    }}
                  >
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 1 }}>
                      <Box
                        sx={{
                          width: 36,
                          height: 36,
                          borderRadius: 2,
                          background: `${stat.color}20`,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          color: stat.color
                        }}
                      >
                        {stat.icon}
                      </Box>
                      <Typography fontSize={12} color="text.secondary" sx={{ flex: 1 }}>
                        {stat.title}
                      </Typography>
                    </Box>
                    <Typography fontWeight={700} fontSize={18}>
                      {stat.value}
                    </Typography>
                  </Paper>
                ))}
              </Box>
            </Box>
          </>
        )}
      </Box>

      {/* CHARTS */}
      <Grid container spacing={isMobile ? 2 : 4} sx={{ mb: { xs: 4, sm: 6 } }}>
        <Grid item xs={12} md={6}>
          <StyledCard>
            <Typography fontWeight={600} sx={{ mb: 2, fontSize: { xs: '1rem', sm: '1.125rem' } }}>
              Comparatif des Revenus et Dépenses
            </Typography>
            <ResponsiveContainer width="100%" height={isMobile ? 250 : 300}>
              <BarChart data={processFinancialData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis 
                  dataKey="name" 
                  tick={{ fontSize: isMobile ? 10 : 12 }}
                  interval={isMobile ? Math.floor(processFinancialData.length / 5) : 0}
                />
                <YAxis tick={{ fontSize: isMobile ? 10 : 12 }} />
                <Tooltip content={<CustomTooltip />} />
                <Bar dataKey="revenue" fill="#6366f1" radius={[4, 4, 0, 0]} />
                <Bar dataKey="expenses" fill="#ef4444" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </StyledCard>
        </Grid>

        <Grid item xs={12} md={6}>
          <StyledCard>
            <Typography fontWeight={600} sx={{ mb: 2, fontSize: { xs: '1rem', sm: '1.125rem' } }}>
              Évolution de la Trésorerie
            </Typography>
            <ResponsiveContainer width="100%" height={isMobile ? 250 : 300}>
              <LineChart data={processFinancialData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis 
                  dataKey="name" 
                  tick={{ fontSize: isMobile ? 10 : 12 }}
                  interval={isMobile ? Math.floor(processFinancialData.length / 5) : 0}
                />
                <YAxis tick={{ fontSize: isMobile ? 10 : 12 }} />
                <Tooltip content={<CustomTooltip />} />
                <Line
                  type="monotone"
                  dataKey="balance"
                  stroke="#10b981"
                  strokeWidth={2}
                  dot={!isMobile}
                  activeDot={{ r: 6 }}
                />
              </LineChart>
            </ResponsiveContainer>
          </StyledCard>
        </Grid>
      </Grid>

      {/* RESTAURANTS */}
      <Typography 
        variant={isMobile ? "h6" : "h5"} 
        fontWeight={600} 
        sx={{ mb: { xs: 2, sm: 3 } }}
      >
        Restaurants Affiliés
      </Typography>

      <Grid container spacing={isMobile ? 2 : 3}>
        {restaurants.map((restaurant) => (
          <Grid item xs={12} sm={6} md={4} key={restaurant.id}>
            <Card
              sx={{
                height: { xs: 180, sm: 200, md: 220 },
                borderRadius: 4,
                overflow: "hidden",
                position: "relative",
                backgroundImage: `linear-gradient(rgba(0,0,0,.6), rgba(0,0,0,.4)), url(${restaurant.imageUrl || 'https://via.placeholder.com/400x200?text=Restaurant'})`,
                backgroundSize: "cover",
                backgroundPosition: "center",
                display: "flex",
                flexDirection: "column",
                justifyContent: "flex-end",
                p: { xs: 2, sm: 3 },
                color: "white",
                cursor: "pointer",
                transition: "transform 0.3s ease",
                '&:hover': {
                  transform: "scale(1.02)"
                }
              }}
              onClick={() => {
                localStorage.setItem('restaurantId', restaurant.id);
                window.location.reload();
              }}
            >
              <Typography fontWeight={700} fontSize={{ xs: '1rem', sm: '1.25rem' }}>
                {restaurant.name}
              </Typography>
              <Typography fontSize={{ xs: 12, sm: 14 }}>
                {restaurant.address}
              </Typography>
              <Typography fontSize={{ xs: 12, sm: 14 }}>
                {restaurant.phone}
              </Typography>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Edit Restaurant Dialog */}
      <Dialog 
        open={isEditDialogOpen} 
        onClose={handleEditClose}
        fullScreen={isMobile}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Modifier le restaurant
          {isMobile && (
            <IconButton
              aria-label="close"
              onClick={handleEditClose}
              sx={{ position: 'absolute', right: 8, top: 8 }}
            >
              <Close />
            </IconButton>
          )}
        </DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            name="name"
            label="Nom du restaurant"
            type="text"
            fullWidth
            variant="outlined"
            value={editFormData.name}
            onChange={handleEditFormChange}
            sx={{ mb: 2, mt: 1 }}
          />
          <TextField
            margin="dense"
            name="address"
            label="Adresse"
            type="text"
            fullWidth
            variant="outlined"
            value={editFormData.address}
            onChange={handleEditFormChange}
            sx={{ mb: 2 }}
          />
          <TextField
            margin="dense"
            name="phone"
            label="Téléphone"
            type="tel"
            fullWidth
            variant="outlined"
            value={editFormData.phone}
            onChange={handleEditFormChange}
            sx={{ mb: 2 }}
          />
          <TextField
            margin="dense"
            name="imageUrl"
            label="URL de l'image"
            type="url"
            fullWidth
            variant="outlined"
            value={editFormData.imageUrl}
            onChange={handleEditFormChange}
          />
        </DialogContent>
        <DialogActions sx={{ p: 2, pt: 0 }}>
          <Button onClick={handleEditClose} color="inherit">
            Annuler
          </Button>
          <Button onClick={handleEditSubmit} variant="contained" color="primary">
            Enregistrer
          </Button>
        </DialogActions>
      </Dialog>

      {/* Mobile Filter Drawer */}
      <SwipeableDrawer
        anchor="bottom"
        open={mobileFilterOpen}
        onClose={toggleMobileFilter}
        onOpen={() => {}}
        swipeAreaWidth={56}
        disableSwipeToOpen={false}
        PaperProps={{
          sx: {
            borderTopLeftRadius: 20,
            borderTopRightRadius: 20,
            p: 3
          }
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6">Filtrer par date</Typography>
          <IconButton onClick={toggleMobileFilter}>
            <Close />
          </IconButton>
        </Box>
        <Divider sx={{ mb: 2 }} />
        <FormControl fullWidth sx={{ mb: 2 }}>
          <InputLabel>Mois</InputLabel>
          <Select
            value={selectedMonth}
            label="Mois"
            onChange={(e) => {
              setSelectedMonth(Number(e.target.value));
              toggleMobileFilter();
            }}
          >
            {Array.from({ length: 12 }, (_, i) => (
              <MenuItem key={i} value={i}>
                {new Date(0, i).toLocaleString("fr-FR", { month: "long" })}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl fullWidth>
          <InputLabel>Année</InputLabel>
          <Select
            value={selectedYear}
            label="Année"
            onChange={(e) => {
              setSelectedYear(Number(e.target.value));
              toggleMobileFilter();
            }}
          >
            {Array.from({ length: 5 }, (_, i) => {
              const year = new Date().getFullYear() - i;
              return <MenuItem key={year} value={year}>{year}</MenuItem>;
            })}
          </Select>
        </FormControl>
      </SwipeableDrawer>
    </Box>
  );
}

export default Dashboard;